import { ProjectCard } from '../../../../../features/projects/components/ProjectCard';
import { ProjectFilterBar } from '../../../../../features/projects/components/ProjectFilterBar';
import { useI18n } from '../../../../../shared/i18n';
import { localePath } from '../../../../../shared/i18n/routing';
import { useData } from '../../../../../shared/lib';
import {
  Container,
  EmptyState,
  Eyebrow,
  Grid,
  Pagination,
  Section,
} from '../../../../../shared/ui';
import type { PortfolioListingData } from './+data';

/**
 * The portfolio listing.
 *
 * Server-rendered with the first page of results already in the markup —
 * `+data.ts` resolved everything before this component runs, so there is no
 * loading state and nothing here depends on hydration. `ProjectFilterBar` and
 * `Pagination` are both plain forms and links, so filtering and paging both
 * work with JavaScript disabled (FR-PORT, HSQ-064's acceptance criterion).
 */
export default function Page() {
  const { locale, t, formatNumber } = useI18n();
  const { listing, filterOptions, heading, query } = useData<PortfolioListingData>();

  const action = localePath(locale, '/projects');

  return (
    <Section spacing="lg">
      <Container className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          {/* A route, not a translated label: it stays Latin and LTR in
              Arabic too, same as any other URL-shaped token (SerpPreview
              does the same for the same reason). */}
          <Eyebrow>
            <span dir="ltr">/projects</span>
          </Eyebrow>
          {/* From the settings registry (LEG-04): team-attributing language,
              editable in the admin, never a hard-coded claim. */}
          <h1 className="max-w-2xl text-4xl font-bold tracking-tight sm:text-5xl">{heading}</h1>
          <p className="hs-meta text-text-subtle">
            {t('common.portfolio.resultCount', { count: formatNumber(listing.meta.total) })}
          </p>
        </div>

        {/* Filter bar and grid on the page's own black — no ink slab behind
            them (see `.hs-ambient-panel` in global.css). */}
        <div className="flex flex-col gap-8">
          <ProjectFilterBar
            options={filterOptions}
            value={queryToFilterValue(query)}
            action={action}
          />

          {listing.data.length > 0 ? (
            <>
              <Grid className="grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                {listing.data.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </Grid>
              <Pagination
                page={listing.meta.page}
                totalPages={listing.meta.total_pages}
                hrefFor={(page) => hrefForPage(action, query, page)}
              />
            </>
          ) : null}
        </div>

        {listing.data.length === 0 && (
          <EmptyState
            title={
              hasActiveFilters(query)
                ? t('common.portfolio.emptyTitle')
                : t('common.portfolio.emptyTitleNoContent')
            }
            description={
              hasActiveFilters(query)
                ? t('common.portfolio.emptyDescription')
                : t('common.portfolio.emptyDescriptionNoContent')
            }
            action={
              hasActiveFilters(query) ? (
                <a href={action} className="py-1 text-sm font-medium text-accent hover:underline">
                  {t('common.actions.clearAll')}
                </a>
              ) : undefined
            }
          />
        )}
      </Container>
    </Section>
  );
}

function queryToFilterValue(query: PortfolioListingData['query']) {
  return {
    q: query.q,
    attribution: query.attribution,
    sort: query.sort,
    yearFrom: query.year_from,
    yearTo: query.year_to,
  };
}

/** Exported for its own test — see tests/unit/portfolioListing.test.tsx. */
export function hasActiveFilters(query: PortfolioListingData['query']): boolean {
  return Boolean(
    query.q ||
    query.technology ||
    query.type ||
    query.attribution ||
    query.year_from ||
    query.year_to,
  );
}

/** Builds the URL for another page, preserving every other active filter. */
export function hrefForPage(
  action: string,
  query: PortfolioListingData['query'],
  page: number,
): string {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.technology) params.set('technology', query.technology);
  if (query.type) params.set('type', query.type);
  if (query.year_from) params.set('year_from', String(query.year_from));
  if (query.year_to) params.set('year_to', String(query.year_to));
  if (query.attribution) params.set('attribution', query.attribution);
  if (query.sort) params.set('sort', query.sort);
  if (page > 1) params.set('page', String(page));

  const search = params.toString();
  return search ? `${action}?${search}` : action;
}
